import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const RecensioniTab = ({ reviews, ratingBreakdown, averageRating, totalReviews }) => {
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [sortBy, setSortBy] = useState('recent');

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const sortedReviews = [...reviews]?.sort((a, b) => {
    if (sortBy === 'recent') {
      return new Date(b.date) - new Date(a.date);
    } else if (sortBy === 'highest') {
      return b?.rating - a?.rating;
    } else {
      return a?.rating - b?.rating;
    }
  });

  const handleSubmitReview = (e) => {
    e?.preventDefault();
    alert('Recensione inviata con successo!');
    setNewReview({ rating: 5, comment: '' });
  };

  return (
    <div className="space-y-6 md:space-y-8 lg:space-y-10">
      <section className="bg-card rounded-lg md:rounded-xl lg:rounded-2xl p-4 md:p-6 lg:p-8 shadow-warm">
        <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground mb-6">
          Valutazione Complessiva
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-primary mb-2">
              {averageRating?.toFixed(1)}
            </div>
            <div className="flex items-center gap-1 mb-2">
              {[1, 2, 3, 4, 5]?.map((star) => (
                <Icon
                  key={star}
                  name="Star"
                  size={24}
                  className={
                    star <= Math.floor(averageRating)
                      ? 'text-primary fill-primary' :'text-muted-foreground'
                  }
                />
              ))}
            </div>
            <p className="text-sm md:text-base text-muted-foreground">
              Basato su {totalReviews} recensioni
            </p>
          </div>

          <div className="space-y-3">
            {ratingBreakdown?.map((item) => (
              <div key={item?.stars} className="flex items-center gap-3">
                <span className="text-sm font-caption font-semibold text-foreground w-12">
                  {item?.stars} stelle
                </span>
                <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-smooth"
                    style={{ width: `${item?.percentage}%` }}
                  />
                </div>
                <span className="text-sm text-muted-foreground w-12 text-right">
                  {item?.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-card rounded-lg md:rounded-xl lg:rounded-2xl p-4 md:p-6 lg:p-8 shadow-warm">
        <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground mb-6">
          Scrivi una Recensione
        </h2>

        <form onSubmit={handleSubmitReview} className="space-y-4 md:space-y-6">
          <div>
            <label className="block text-sm font-caption font-semibold text-foreground mb-3">
              La tua valutazione
            </label>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5]?.map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setNewReview({ ...newReview, rating: star })}
                  className="transition-smooth hover:scale-110"
                >
                  <Icon
                    name="Star"
                    size={32}
                    className={
                      star <= newReview?.rating
                        ? 'text-primary fill-primary' :'text-muted-foreground'
                    }
                  />
                </button>
              ))}
            </div>
          </div>

          <Input
            label="Il tuo commento"
            type="text"
            placeholder="Condividi la tua esperienza..."
            value={newReview?.comment}
            onChange={(e) => setNewReview({ ...newReview, comment: e?.target?.value })}
            required
          />

          <Button
            variant="default"
            size="default"
            iconName="Send"
            iconPosition="left"
            type="submit"
          >
            Invia Recensione
          </Button>
        </form>
      </section>
      <section>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground">
            Recensioni Clienti
          </h2>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Ordina per:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              className="px-4 py-2 rounded-lg border border-input bg-background text-foreground text-sm transition-smooth focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3"
            >
              <option value="recent">Più recenti</option>
              <option value="highest">Voto più alto</option>
              <option value="lowest">Voto più basso</option>
            </select>
          </div>
        </div>

        <div className="space-y-4 md:space-y-6">
          {sortedReviews?.map((review) => (
            <div
              key={review?.id}
              className="bg-card rounded-lg md:rounded-xl p-4 md:p-6 shadow-warm"
            >
              <div className="flex items-start gap-3 md:gap-4 mb-4">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full overflow-hidden flex-shrink-0">
                  <Image
                    src={review?.userAvatar}
                    alt={review?.userAvatarAlt}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
                    <h4 className="text-base md:text-lg font-heading font-bold text-foreground">
                      {review?.userName}
                    </h4>
                    <span className="text-xs md:text-sm text-muted-foreground">
                      {formatDate(review?.date)}
                    </span>
                  </div>

                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5]?.map((star) => (
                      <Icon
                        key={star}
                        name="Star"
                        size={16}
                        className={
                          star <= review?.rating
                            ? 'text-primary fill-primary' :'text-muted-foreground'
                        }
                      />
                    ))}
                  </div>

                  <p className="text-sm md:text-base text-foreground leading-relaxed">
                    {review?.comment}
                  </p>

                  {review?.response && (
                    <div className="mt-4 p-3 md:p-4 bg-muted rounded-lg">
                      <p className="text-xs md:text-sm font-caption font-semibold text-foreground mb-2">
                        Risposta del proprietario:
                      </p>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        {review?.response}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default RecensioniTab;